// src/lib/use-base-price.js — Voie B+ (prix de base)
//
// Hook léger pour gérer `logement_pricing_config.base_price` côté UI :
//
//   - GET au mount : fetch /api/beds24-mapping?action=pricing_logement_config
//     &logement_id=X → récupère base_price + base_price_suggestion +
//     pricing_mode + hasMapping.
//   - save(value) : POST ?action=pricing_set_base_price → UPDATE DB (pas de
//     push Beds24). value NULL accepté (clear).
//   - applyBase({ date_from, date_to, base_price, overwrite_existing,
//     confirmStage }) : POST ?action=pricing_apply_base.
//       - confirmStage absent/false → renvoie le récap (confirm_required,
//         total_dates, deja_tarifees, value) SANS push.
//       - confirmStage=true → push réel (ou DRY_RUN) + upsert daily_pricing
//         SANS verrou (is_manual_override=false).
//
// Mode-gated côté UI : `pricingMode` exposé pour griser champ + bouton si
// pricing_mode !== 'manual' (dormant en pricelabs).
//
// AbortController au unmount.

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabase";

const LOGEMENT_ID_REGEX = /^[a-z0-9.]+$/i;

/**
 * Hook mono-logement pour le prix de base (Voie B+).
 *
 * @param {string} logementId
 * @returns {{
 *   basePrice: number|null,
 *   suggestion: number|null,
 *   pricingMode: 'pricelabs'|'manual'|null,
 *   hasMapping: boolean,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: () => Promise<void>,
 *   save: (value: number|null) => Promise<{ok:boolean, error?:string, basePrice?:number|null}>,
 *   applyBase: (args: {date_from:string, date_to:string, base_price:number, overwrite_existing:boolean, confirmStage?:boolean}) => Promise<object>,
 * }}
 */
export function useBasePrice(logementId) {
  const [state, setState] = useState({
    basePrice: null,
    suggestion: null,
    pricingMode: null,
    hasMapping: false,
    loading: false,
    error: null,
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchConfig = useCallback(async () => {
    if (!logementId || !LOGEMENT_ID_REGEX.test(logementId)) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        if (!mountedRef.current) return;
        setState((s) => ({ ...s, loading: false, error: "not_authenticated" }));
        return;
      }
      const qs = new URLSearchParams({
        action: "pricing_logement_config",
        logement_id: logementId,
      });
      const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        if (!mountedRef.current) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: json.error || `http_${res.status}`,
        }));
        return;
      }
      const json = await res.json();
      if (!mountedRef.current) return;
      const row = (json.configs || []).find(
        (c) => c.logement_id === logementId,
      );
      setState((s) => ({
        ...s,
        basePrice: row?.base_price ?? null,
        suggestion: json.base_price_suggestion ?? null,
        pricingMode: row?.pricing_mode ?? null,
        hasMapping: !!row?.beds24_room_id,
        loading: false,
        error: null,
      }));
    } catch (e) {
      if (!mountedRef.current) return;
      setState((s) => ({
        ...s,
        loading: false,
        error: e.message || String(e),
      }));
    }
  }, [logementId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const save = useCallback(
    async (newValue) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          return { ok: false, error: "not_authenticated" };
        }
        const res = await fetch(
          "/api/beds24-mapping?action=pricing_set_base_price",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              logement_id: logementId,
              base_price: newValue,
            }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          return {
            ok: false,
            status: res.status,
            error: json.error || json.message || `http_${res.status}`,
          };
        }
        if (mountedRef.current) {
          setState((s) => ({ ...s, basePrice: json.base_price ?? null }));
        }
        return { ok: true, basePrice: json.base_price ?? null };
      } catch (e) {
        return { ok: false, error: e.message || String(e) };
      }
    },
    [logementId],
  );

  const applyBase = useCallback(
    async ({
      date_from,
      date_to,
      base_price,
      overwrite_existing,
      confirmStage = false,
    }) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          return { ok: false, error: "not_authenticated" };
        }
        const res = await fetch(
          "/api/beds24-mapping?action=pricing_apply_base",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              logement_id: logementId,
              date_from,
              date_to,
              base_price,
              overwrite_existing: overwrite_existing === true,
              confirmStage: confirmStage === true,
            }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          return {
            ok: false,
            status: res.status,
            // 2026-09-15 : la PHRASE d'abord (un refus Beds24 porte son motif),
            // le code sinon — l'admin lit « Beds24 a refusé : … », pas
            // « beds24_refused ».
            error: json.message || json.error || `http_${res.status}`,
            code: json.error || null,
            verdict: json.verdict || null,
          };
        }
        return { ok: true, ...json };
      } catch (e) {
        return { ok: false, error: e.message || String(e) };
      }
    },
    [logementId],
  );

  return {
    basePrice: state.basePrice,
    suggestion: state.suggestion,
    pricingMode: state.pricingMode,
    hasMapping: state.hasMapping,
    loading: state.loading,
    error: state.error,
    refetch: fetchConfig,
    save,
    applyBase,
  };
}
